import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import CreationToolColorPickerColor from '../types/color-picker-color'
import CreationToolUIAnimation from '../types/ui-animation'
import { normalizeHexLength } from '../utils/color-utils'
import { GridBlock } from './blocks'
import { ToolButton } from './buttons'
import ColorInputComponent from './color-input'
import { CheckIcon } from './icons'

const baseStyles = css`
    align-items: center;
    border-radius: 6px;
    display: flex;
    height: 30px;
    justify-content: center;
    text-align: center;
`

const Color = styled(ToolButton)`
    ${baseStyles}
    box-shadow: inset 0 0 0 2px;
    color: transparent;
    grid-column: span 1;

    &:last-child {
        margin-right: 0;
    }
`

const None = styled(ToolButton)`
    ${baseStyles}
    background: #000;
    box-shadow: inset 0 0 0 1px;
    color: #fff;
    font-size: 13px;
    font-weight: bold;
    grid-column: span 2;
    line-height: 0;
`

const Check = styled(CheckIcon)`
    ${CreationToolUIAnimation.css(CreationToolUIAnimation.PopIn)}
`

const ColorInput = styled(ColorInputComponent)`
    margin-top: 15px;
    grid-column: span 2;
`

type Props = {
    className?: string
    defaultColor?: string
    columns?: number
    allowEmpty?: boolean
    showInput?: boolean
    onColorChange: (color: string) => void
}

const CreationToolsColorPicker = ({
    className,
    defaultColor = '',
    columns = 6,
    allowEmpty = false,
    showInput = false,
    onColorChange,
}: Props) => {
    const [activeColor, setActiveColor] = useState<string>(defaultColor)
    const allColors = CreationToolColorPickerColor.all()
    const isCustomColor = !allColors.find((color) => CreationToolColorPickerColor.color(color) === activeColor)

    useEffect(() => {
        onColorChange(activeColor)
    }, [activeColor])

    return (
        <div className={className}>
            <GridBlock columns={columns}>
                {allowEmpty && <None onClick={() => setActiveColor('')}>None</None>}
                {allColors.map((pickerColor) => {
                    const color = CreationToolColorPickerColor.color(pickerColor)
                    const borderColor = CreationToolColorPickerColor.borderColor(pickerColor)
                    const background = CreationToolColorPickerColor.css(pickerColor)
                    const isActive = activeColor && normalizeHexLength(activeColor) === normalizeHexLength(color)
                    return (
                        <Color
                            key={pickerColor}
                            style={{
                                background,
                                color: borderColor, // setting this will make the box-shadow use this color
                            }}
                            onClick={() => setActiveColor(color)}
                        >
                            {isActive && <Check />}
                        </Color>
                    )
                })}
            </GridBlock>
            {showInput && (
                <GridBlock columns={columns}>
                    <ColorInput defaultValue={isCustomColor ? activeColor : undefined} onColorChange={onColorChange} />
                </GridBlock>
            )}
        </div>
    )
}

export default CreationToolsColorPicker
